import {inject, Injectable} from '@angular/core';
import {CmsApiService} from "./cms-api.service";
import {Reference} from "./definitions/reference";
import {MetaField} from "./definitions/meta-field";
import {FieldParameters} from "./definitions/field-parameters";
import {SearchObject} from "./definitions/search-object";
import {SuperObjectModel} from "./definitions/super-object-model";
import {LoggerService} from "./logger.service";

export interface ReferenceUsageInfo {
  usage: SearchObject[],
  showUsageText: string;
  usageWarning: string;
  usageConfirmStoreText: string;
}
@Injectable({
  providedIn: 'root'
})
export class SearchReferenceService {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);

  private searchReferences: {[id: string]: Reference};

  async setSearchReferences(): Promise<void> {
    this.searchReferences = await this.cms.getAllSearchReferences();
  }

  getSearchReferenceFromField(metaField: Partial<MetaField>, rootObject: any = null): Reference {
    if (!metaField) {
      return null;
    }
    if (metaField.reference && !metaField.reference.reference_id) {
      return metaField.reference;
    }
    const referenceId = this.getReferenceId(metaField);
    if (!referenceId) {
      return null;
    }
    const ref = this.getSearchReferenceFromReferenceId(referenceId);
    const refCopy = ref ? {...ref} : null;
    if (refCopy && ref.object_type === '{reference_object_type}') {
      if (rootObject) {
        ref.object_type = rootObject.reference_object_type;
      } else {
        this.logger.warn('SearchReferenceService: rootObject is required to resolve reference object type for reference ID ' + referenceId);
      }
    }
    return refCopy;
  }

  getSearchReferenceFromReferenceId(referenceId: string): Reference {
    if (this.searchReferences) {
      return this.searchReferences[referenceId];
    } else {
      this.setSearchReferences().then();
      return null;
    }
  }

  async getReferenceUsage(fieldParameters: FieldParameters, parentObject?: SuperObjectModel): Promise<ReferenceUsageInfo> {
    let res = {
      usage: [],
      showUsageText: '',
      usageWarning: ''
    } as ReferenceUsageInfo;
    const ref = this.getSearchReferenceFromField(fieldParameters.field);
    if (ref?.show_usage && fieldParameters.object) {
      res.showUsageText = ref.show_usage_text || 'TRANS__USAGE_COUNT';
      res.usageWarning = ref.usage_warning;
      res.usageConfirmStoreText = ref.usage_confirm_store_text;
      res.usage = await this.cms.searchForReferenceUsage({
        ref_object_type: fieldParameters.field.inline.model,
        ref_parent_field: fieldParameters.field.name,
        ref_id_field: ref.ref_prop,
        usage_object_id: fieldParameters.object.artifact_id,
        parent_object_id: parentObject?.artifact_id});
    }
    return res;
  }

  private getReferenceId(metaField: Partial<MetaField>) {
    let referenceId = metaField.reference_id;
    if (!referenceId && metaField.reference?.reference_id) {
      // This only happens when field comes from search results
      referenceId = metaField.reference.reference_id;
    }
    return referenceId;
  }
}
